import React, { useEffect, useState } from 'react';
import { Loader2, Trash2, Link2, X, Search, Clock, Timer } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TimeRangeSlider } from './TimeRangeSlider';
import { fromHHMM, toHHMM, fmtHours, parseIso, fmtDay, textOn } from './utils';
import { PhotoAttach } from './PhotoAttach';

const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240, 300, 360, 420, 480];

function LinkPicker({ link, onChange }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(async () => {
      const r = await fetch(`${API_URL}/api/timesheets/link-search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
      if (r.ok) setHits(await r.json());
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (link) {
    return (
      <div className="inline-flex items-center gap-2 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-full px-3 py-1" data-testid="ts-link-chip">
        <Link2 className="w-3 h-3" /> {link.label}
        <button type="button" onClick={() => onChange(null)} aria-label="Remove link"><X className="w-3 h-3" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Link to a course, project or support ticket (optional)" className="pl-9 h-9 text-sm" data-testid="ts-link-search" />
      {hits.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm">
          {hits.map((h) => (
            <li key={`${h.type}-${h.id}`}>
              <button type="button" onClick={() => { onChange(h); setQ(''); setHits([]); }} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2" data-testid={`ts-link-hit-${h.id}`}>
                <span className="text-[10px] uppercase font-bold text-gray-400 w-12">{h.type}</span>{h.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function EntryDialog({ open, onClose, entry, date, jobTypes, people, canPickPerson, defaultUserId, onSaved, onDeleted }) {
  const editing = !!entry?.id;
  const [mode, setMode] = useState('range');
  const [range, setRange] = useState([9 * 60, 12 * 60]);
  const [minutes, setMinutes] = useState(60);
  const [jobTypeId, setJobTypeId] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [link, setLink] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [userId, setUserId] = useState(defaultUserId || '');
  const [entryDate, setEntryDate] = useState(date);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (entry?.id) {
      setMode(entry.start ? 'range' : 'duration');
      setRange(entry.start ? [fromHHMM(entry.start), fromHHMM(entry.end)] : [9 * 60, 9 * 60 + (entry.minutes || 60)]);
      setMinutes(entry.minutes || 60); setJobTypeId(entry.job_type_id); setDescription(entry.description || '');
      setNotes(entry.notes || ''); setLink(entry.link || null); setPhotos(entry.photos || []); setUserId(entry.user_id); setEntryDate(entry.date);
    } else {
      setMode('range'); setRange(entry?.start ? [fromHHMM(entry.start), fromHHMM(entry.end)] : [9 * 60, 12 * 60]); setMinutes(60);
      setJobTypeId(jobTypes[0]?.id || ''); setDescription(''); setNotes(''); setLink(null); setPhotos([]); setUserId(defaultUserId || ''); setEntryDate(date);
    }
  }, [open, entry, date, jobTypes, defaultUserId]);

  const jt = jobTypes.find((j) => j.id === jobTypeId);
  const colour = jt?.colour || '#3b2508';

  const save = async () => {
    if (!jobTypeId) { toast.error('Pick a job type'); return; }
    setBusy(true);
    try {
      const body = { date: entryDate, job_type_id: jobTypeId, description, notes, link, photo_ids: photos, user_id: userId || undefined };
      if (mode === 'range') { body.start = toHHMM(range[0]); body.end = toHHMM(range[1]); } else { body.minutes = minutes; }
      const r = await fetch(`${API_URL}/api/timesheets/entries${editing ? `/${entry.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success(editing ? 'Entry updated' : `Logged ${fmtHours(d.minutes)} on ${d.job_type_name}`);
      onSaved(d); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this time entry?')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/timesheets/entries/${entry.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Entry deleted'); onDeleted(entry.id); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="ts-entry-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit time' : 'Log time'} · {entryDate ? fmtDay(parseIso(entryDate)) : ''}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="h-9 w-auto" data-testid="ts-entry-date" />
            {canPickPerson && people.length > 1 && (
              <select value={userId} onChange={(e) => setUserId(e.target.value)} className="h-9 border rounded-md px-2 text-sm bg-white" data-testid="ts-entry-person" disabled={editing}>
                {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
              </select>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">What were you doing?</div>
            <div className="flex flex-wrap gap-1.5" data-testid="ts-job-types">
              {jobTypes.filter((j) => j.active !== false || j.id === jobTypeId).map((j) => (
                <button key={j.id} type="button" onClick={() => setJobTypeId(j.id)} data-testid={`ts-job-type-${j.id}`}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-transform active:scale-95 ${j.id === jobTypeId ? 'shadow-md scale-105' : 'opacity-70 hover:opacity-100'}`}
                  style={j.id === jobTypeId ? { background: j.colour, color: textOn(j.colour), borderColor: j.colour } : { borderColor: j.colour, color: j.colour, background: '#fff' }}>
                  {j.name}
                </button>
              ))}
            </div>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Tidying the warehouse, prepping kits for Monday…" className="mt-2 h-10" maxLength={500} data-testid="ts-entry-description" />
          </div>

          <div>
            <div className="flex gap-1 mb-3 rounded-lg bg-gray-100 p-1 w-fit" role="tablist">
              {[['range', Clock, 'Start → end'], ['duration', Timer, 'Just hours']].map(([k, Icon, l]) => (
                <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)} data-testid={`ts-mode-${k}`}
                  className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md ${mode === k ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
                  <Icon className="w-3.5 h-3.5" /> {l}
                </button>
              ))}
            </div>
            {mode === 'range' ? (
              <TimeRangeSlider value={range} onChange={setRange} colour={colour} />
            ) : (
              <div className="space-y-2">
                <div className="text-3xl font-black tabular-nums" data-testid="ts-duration-value">{fmtHours(minutes)}</div>
                <div className="flex flex-wrap gap-1.5">
                  {DURATIONS.map((m) => (
                    <button key={m} type="button" onClick={() => setMinutes(m)} data-testid={`ts-duration-${m}`}
                      className={`text-xs font-bold px-2.5 py-1.5 rounded-full border ${minutes === m ? 'text-white' : 'bg-white text-gray-700 border-gray-300'}`}
                      style={minutes === m ? { background: colour, borderColor: colour } : {}}>{fmtHours(m)}</button>
                  ))}
                </div>
                <input type="range" min={15} max={720} step={15} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} className="w-full h-8 accent-[#3b2508]" data-testid="ts-duration-slider" />
              </div>
            )}
          </div>

          <LinkPicker link={link} onChange={setLink} />
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2} className="text-sm" data-testid="ts-entry-notes" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Photos</div>
            <PhotoAttach photos={photos} onChange={setPhotos} disabled={busy} />
          </div>

          <div className="flex items-center gap-2 pt-1">
            {editing && (
              <Button variant="ghost" onClick={remove} disabled={busy} className="text-rose-600 hover:bg-rose-50 mr-auto" data-testid="ts-entry-delete"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
            )}
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="ts-entry-cancel" className={editing ? '' : 'ml-auto'}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="text-white font-bold" style={{ background: colour }} data-testid="ts-entry-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} {editing ? 'Save changes' : `Log ${mode === 'range' ? fmtHours(range[1] - range[0]) : fmtHours(minutes)}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
